import { useCallback, useState, useEffect, useMemo } from 'react';
import '@glideapps/glide-data-grid/dist/index.css';
import {
  CompactSelection,
  CustomCell,
  DataEditor,
  GridCell,
  GridCellKind,
  GridColumn,
  GridSelection,
  Item,
  SpriteMap
} from '@glideapps/glide-data-grid';
import { useLayer } from 'react-laag';
import {
  useAddRow,
  useDeleteColumn,
  useDeleteRow,
  useExecuteWorkflow,
  useGetColumns,
  useGetInfiniteRows,
  useUpdateCell,
  useUpdateColumn,
  useUpdateRow
} from 'apis/business/agent/worksheet/hook';
import { WorksheetResponse } from 'apis/business/agent/worksheet/types';
import { EmptyStateView } from 'components/Common/EmptyStateView';
import {
  Box,
  debounce,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Typography,
  useTheme
} from '@mui/material';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { MdOutlineEdit } from 'react-icons/md';
import Button from 'components/Common/Button';
import { formatDate, formatDateLocal } from 'utils/helperFunctions';
import ColumnDrawer, { ColumnFormData } from './Components/ColumnDrawer';
import { FaRegEyeSlash } from 'react-icons/fa6';
import CallLogForm from '../CallLogs/components/CallLogForm';
import { datetimeRenderer, getColumnTypeIcon, singleSelectRenderer } from './Components/worksheetCellRenderers';
import { SelectOption } from 'apis/business/agent/worksheet/types';

interface DummyItem {
  row_id?: number;
  workflow_logs?: Record<string, any>;
  [key: string]: string | number | Record<string, any> | undefined;
}

interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export default function WorksheetTable({
  worksheetData,
  filters,
  sorting,
  handleFreezeColumnChange,
  selectedHeight
}: {
  worksheetData: WorksheetResponse | undefined;
  filters: any;
  sorting: any;
  handleFreezeColumnChange: number;
  selectedHeight: number;
}) {
  const theme = useTheme();
  const [data, setData] = useState<DummyItem[]>([]);
  const [currentColumns, setCurrentColumns] = useState<any[]>([]);
  const [menu, setMenu] = useState<{ col: number; bounds: Rectangle }>();
  const [columnID, setColumnID] = useState<string>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInitialData, setDrawerInitialData] = useState<any>(null);
  const [callLogDrawerOpen, setCallLogDrawerOpen] = useState(false);
  const [callLogId, setCallLogId] = useState('');
  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty()
  });

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  useEffect(() => {
    let portal = document.getElementById('portal');
    let created = false;

    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'portal';
      document.body.appendChild(portal);
      created = true;
    }

    return () => {
      if (created && portal?.parentNode) {
        portal.parentNode.removeChild(portal);
      }
    };
  }, []);

  const { data: columnsData } = useGetColumns();
  const {
    data: rowsInfiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useGetInfiniteRows({ include: 'cells', ...filters }, sorting);

  const rowsData = useMemo(
    () => ({
      data: rowsInfiniteData?.pages.flatMap((page) => page.data) ?? [],
      meta: rowsInfiniteData?.pages[rowsInfiniteData.pages.length - 1]?.meta
    }),
    [rowsInfiniteData]
  );

  const totalRowCount = rowsInfiniteData?.pages[0]?.meta?.total_count ?? rowsData.data.length;

  const { mutateAsync: handleAddRow } = useAddRow();
  const { mutateAsync: handleUpdateCell } = useUpdateCell();
  const { mutateAsync: handleUpdateColumn } = useUpdateColumn();
  const { mutateAsync: handleUpdateRow } = useUpdateRow();
  const { mutateAsync: handleDeleteColumn } = useDeleteColumn();
  const { mutateAsync: handleDeleteRow } = useDeleteRow();
  const { mutateAsync: handleExecuteWorkflow } = useExecuteWorkflow();

  const isOpen = menu !== undefined;
  const { layerProps, renderLayer } = useLayer({
    isOpen,
    auto: true,
    placement: 'bottom-end',
    triggerOffset: 2,
    onOutsideClick: () => setMenu(undefined),
    trigger: {
      getBounds: () => ({
        left: menu?.bounds.x ?? 0,
        top: menu?.bounds.y ?? 0,
        width: menu?.bounds.width ?? 0,
        height: menu?.bounds.height ?? 0,
        right: (menu?.bounds.x ?? 0) + (menu?.bounds.width ?? 0),
        bottom: (menu?.bounds.y ?? 0) + (menu?.bounds.height ?? 0)
      })
    }
  });

  const handleOpenCallLogModal = (id: string) => {
    setCallLogId(id);
    setCallLogDrawerOpen(true);
  };

  const customHeaderIcons: SpriteMap = {
    customTextIcon: () => `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <mask id="mask0_7281_8663" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
          <rect width="20" height="20" fill="#D9D9D9"/>
        </mask>
        <g mask="url(#mask0_7281_8663)">
          <path d="M6.16667 16.5963V5.76292H2V4H12.0963V5.76292H7.92958V16.5963H6.16667ZM13.6667 16.5963V9.92958H11.1667V8.16667H17.9296V9.92958H15.4296V16.5963H13.6667Z" fill="#4B22A8"/>
        </g>
      </svg>
    `,
    customDateTimeIcon: () => `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 28 26" fill="white">
        <circle cx="12" cy="12" r="10" stroke="#4B22A8" stroke-width="2" fill="white"/>
        <path d="M12 6v6l4 2" stroke="#4B22A8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `,
    customNumberIcon: () => `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M7.2 16.5L8.1 3.5H9.7L8.8 16.5H7.2ZM11.3 16.5L12.2 3.5H13.8L12.9 16.5H11.3ZM4.2 12.4L4.5 10.9H15.3L15 12.4H4.2ZM4.8 8.6L5.1 7.1H15.9L15.6 8.6H4.8Z" fill="#4B22A8"/>
      </svg>
    `,
    customSelectIcon: () => `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="7.5" cy="5" r="1.25" fill="#4B22A8"/>
        <circle cx="12.5" cy="5" r="1.25" fill="#4B22A8"/>
        <circle cx="7.5" cy="10" r="1.25" fill="#4B22A8"/>
        <circle cx="12.5" cy="10" r="1.25" fill="#4B22A8"/>
        <circle cx="7.5" cy="15" r="1.25" fill="#4B22A8"/>
        <circle cx="12.5" cy="15" r="1.25" fill="#4B22A8"/>
      </svg>
    `,
    customActionIcon: () => `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M13.4258 20.6626H9.04658C8.54831 20.6626 8.54831 19.9152 9.04658 19.9152H13.5808L13.9961 17.9H1.9324C1.43966 17.9 1.43966 17.147 1.9324 17.147H14.1511L14.5608 15.1374H10.331C9.83828 15.1374 9.83828 14.3844 10.331 14.3844H13.985L11.7096 12.3692H0.43205C-0.0606834 12.3692 -0.0606834 11.6218 0.43205 11.6218H10.9068C10.63 11.2176 10.6687 10.6585 11.0009 10.2931L11.621 9.60655H9.04658C8.54831 9.60655 8.54831 8.85361 9.04658 8.85361H12.2964L14.1179 6.84392H1.9324C1.43966 6.84392 1.43966 6.09098 1.9324 6.09098H14.7933L16.6147 4.07576H11.1559C10.6577 4.07576 10.6577 3.32835 11.1559 3.32835H17.2957L19.5213 0.859148C20.2521 0.0508439 21.5919 0.731813 21.3705 1.80033L19.7871 9.29098L23.5795 12.4578C24.0556 12.8564 24.0943 13.5706 23.668 14.0245L14.9428 23.154C14.1954 23.929 12.8999 23.2481 13.1158 22.1962L13.4258 20.6626ZM20.0805 1.36295L11.5545 10.7969C11.4327 10.9297 11.4494 11.1346 11.5822 11.2508L15.2639 14.5173C15.3635 14.6059 15.4134 14.7387 15.3857 14.8716L13.8521 22.3457C13.7857 22.6612 14.1788 22.8661 14.4002 22.6335L23.1255 13.5041C23.2583 13.3657 23.2473 13.1553 23.1033 13.028L19.1338 9.72282C19.0286 9.63424 18.9788 9.49029 19.0064 9.35742L20.6341 1.64531C20.7006 1.3242 20.3019 1.11936 20.0805 1.36295ZM8.63689 4.07576H5.48119C4.98845 4.07576 4.98845 3.32835 5.48119 3.32835H8.63689C9.12963 3.32835 9.12963 4.07576 8.63689 4.07576ZM7.10887 9.60655H5.48119C4.98845 9.60655 4.98845 8.85361 5.48119 8.85361H7.10887C7.60714 8.85361 7.60714 9.60655 7.10887 9.60655ZM8.10541 15.1374H5.48119C4.98845 15.1374 4.98845 14.3844 5.48119 14.3844H8.10541C8.59814 14.3844 8.59814 15.1374 8.10541 15.1374ZM7.40229 20.6626H5.48119C4.98845 20.6626 4.98845 19.9152 5.48119 19.9152H7.40229C7.89503 19.9152 7.89503 20.6626 7.40229 20.6626Z" fill="#4B22A8"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.5788 11.249L15.2605 14.5154C15.3601 14.604 15.41 14.7369 15.3823 14.8697L13.8487 22.3438C13.7823 22.6593 14.1754 22.8642 14.3968 22.6317L23.1221 13.5023C23.2549 13.3638 23.2439 13.1535 23.0999 13.0261L19.1304 9.72094C19.0252 9.63236 18.9754 9.48841 19.003 9.35554L20.6307 1.64343C20.6972 1.32232 20.2985 1.11748 20.0771 1.36108L11.5511 10.795C11.4293 10.9279 11.4459 11.1327 11.5788 11.249Z" fill="#E4DEF2"/>
</svg>

    `,
    customWorkflowIcon: () => `
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <g clip-path="url(#clip0_7274_15853)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M11.1791 17.2208H7.5297C7.11448 17.2208 7.11448 16.598 7.5297 16.598H11.3083L11.6543 14.9186H1.60122C1.1906 14.9186 1.1906 14.2912 1.60122 14.2912H11.7835L12.1249 12.6164H8.60006C8.18945 12.6164 8.18945 11.989 8.60006 11.989H11.645L9.74885 10.3096H0.350927C-0.0596841 10.3096 -0.0596841 9.68677 0.350927 9.68677H9.07988C8.8492 9.34998 8.88149 8.884 9.15831 8.5795L9.67503 8.00741H7.5297C7.11448 8.00741 7.11448 7.37996 7.5297 7.37996H10.2379L11.7558 5.70522H1.60122C1.1906 5.70522 1.1906 5.07777 1.60122 5.07777H12.3186L13.8365 3.39842H9.28749C8.87226 3.39842 8.87226 2.77558 9.28749 2.77558H14.404L16.2587 0.71791C16.8677 0.0443231 17.9841 0.611797 17.7996 1.50222L16.4801 7.74444L19.6404 10.3834C20.0372 10.7156 20.0695 11.3108 19.7142 11.6891L12.4432 19.2969C11.8204 19.9428 10.7408 19.3754 10.9207 18.4988L11.1791 17.2208ZM16.7246 1.13775L9.61967 8.99934C9.51817 9.11007 9.53201 9.28077 9.64274 9.37766L12.7108 12.0997C12.7938 12.1735 12.8354 12.2842 12.8123 12.395L11.5343 18.6233C11.479 18.8863 11.8065 19.057 11.9911 18.8632L19.2621 11.2554C19.3728 11.1401 19.3636 10.9647 19.2437 10.8586L15.9357 8.1043C15.848 8.03048 15.8065 7.91053 15.8296 7.7998L17.186 1.37304C17.2414 1.10545 16.9092 0.93475 16.7246 1.13775ZM7.1883 3.39842H4.55854C4.14793 3.39842 4.14793 2.77558 4.55854 2.77558H7.1883C7.59891 2.77558 7.59891 3.39842 7.1883 3.39842ZM5.91494 8.00741H4.55854C4.14793 8.00741 4.14793 7.37996 4.55854 7.37996H5.91494C6.33017 7.37996 6.33017 8.00741 5.91494 8.00741ZM6.74539 12.6164H4.55854C4.14793 12.6164 4.14793 11.989 4.55854 11.989H6.74539C7.156 11.989 7.156 12.6164 6.74539 12.6164ZM6.15946 17.2208H4.55854C4.14793 17.2208 4.14793 16.598 4.55854 16.598H6.15946C6.57007 16.598 6.57007 17.2208 6.15946 17.2208Z" fill="#4B22A8"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.6425 9.37609L12.7106 12.0981C12.7936 12.1719 12.8351 12.2827 12.8121 12.3934L11.5341 18.6218C11.4787 18.8847 11.8063 19.0554 11.9908 18.8617L19.2619 11.2538C19.3726 11.1385 19.3634 10.9632 19.2434 10.8571L15.9355 8.10274C15.8478 8.02892 15.8063 7.90896 15.8294 7.79824L17.1858 1.37148C17.2411 1.10389 16.9089 0.933185 16.7244 1.13618L9.61943 8.99778C9.51793 9.1085 9.53177 9.27921 9.6425 9.37609Z" fill="#EAE6FD"/>
      </g>
      <defs>
        <clipPath id="clip0_7274_15853">
          <rect width="20" height="20" fill="white"/>
        </clipPath>
      </defs>
    </svg>
    `
  };

  useEffect(() => {
    const defaultColumns: GridColumn[] | any = [
      {
        title: 'DATE & TIME',
        id: 'created_at',
        width: 200,
        is_default: true,
        data_type: 'text',
        icon: 'customDateTimeIcon'
      }
    ];

    if (columnsData?.data?.length) {
      const customColumns: GridColumn[] | any = columnsData.data
        .filter((col: any) => col.data_type !== 'action')
        .map((col: any) => ({
          title: col.name,
          description: col.description,
          id: col.id.toString(),
          width: col?.configuration?.width ?? 200,
          hasMenu: !col.is_default,
          is_default: col.is_default,
          required: col.required,
          data_type: col.data_type,
          configuration: col.configuration || {},
          options: col.options || [],
          icon: getColumnTypeIcon(col.data_type)
        }));

      const actionColumn: GridColumn[] | any = columnsData.data
        .filter((col: any) => col.data_type === 'action')
        .map((col: any) => ({
          title: 'Action',
          description: col.description,
          id: col.id.toString(),
          width: col?.configuration?.width ?? 200,
          hasMenu: !col.is_default,
          is_default: col.is_default,
          required: col.required,
          data_type: col.data_type,
          configuration: col.configuration || {},
          icon: 'customActionIcon'
        }));

      defaultColumns.push(...customColumns, ...actionColumn);
    }

    setCurrentColumns(defaultColumns);

    if (rowsData?.data?.length) {
      const extractedData: DummyItem[] = rowsData.data.reduce((acc: DummyItem[], row: any) => {
        const rowObject: DummyItem = {
          row_id: row.id,
          created_at: formatDate(row.created_at),
          workflow_logs: {}
        };

        if (columnsData?.data?.length) {
          columnsData.data.forEach((col: any) => {
            const cell = row.cells?.find((c: any) => c.column_id === col.id);
            rowObject[col.id.toString()] = cell ? cell.value || '' : '';
            if (cell?.workflow_execution_log) {
              rowObject.workflow_logs![col.id.toString()] = cell.workflow_execution_log;
            }
          });
        }

        acc.push(rowObject);
        return acc;
      }, []);

      setData(extractedData);
    } else {
      setData([]);
    }
  }, [columnsData, rowsData?.data]);

  const getCellContent = useCallback(
    (cell: Item): GridCell => {
      const [col, row] = cell;
      const columnData = currentColumns[col];
      const colId = currentColumns[col]?.id;
      const dataRow = data[row];

      if (columnData?.data_type === 'action') {
        return {
          kind: GridCellKind.Custom,
          allowOverlay: false,
          readonly: true,
          copyData: '',
          data: {
            name: 'interaction',
            id: dataRow?.[colId]
          }
        };
      }

      if (columnData?.data_type === 'app_execution') {
        return {
          kind: GridCellKind.Custom,
          allowOverlay: true,
          readonly: true,
          copyData: colId,
          data: {
            name: 'workflow',
            rowId: dataRow?.row_id!,
            columnId: colId,
            workFlowLog: dataRow?.workflow_logs?.[colId]
          }
        };
      }

      if (!colId || !dataRow) {
        return {
          kind: GridCellKind.Text,
          allowOverlay: true,
          readonly: false,
          displayData: '',
          data: '',
          contentAlign: 'center'
        };
      }

      const cellData = String(dataRow[colId] || '');

      if (columnData?.data_type === 'number') {
        const numericValue = cellData === '' ? undefined : Number(cellData);
        return {
          kind: GridCellKind.Number,
          allowOverlay: true,
          readonly: false,
          displayData: cellData,
          data: Number.isFinite(numericValue) ? numericValue : undefined,
          contentAlign: 'left'
        };
      }

      if (columnData?.data_type === 'single_select') {
        const options: SelectOption[] = columnData?.options || [];
        const selected = options.find((option) => option.id === cellData);
        return {
          kind: GridCellKind.Custom,
          allowOverlay: true,
          readonly: false,
          activationBehaviorOverride: 'single-click',
          copyData: selected?.label || '',
          data: {
            name: 'single_select',
            value: cellData,
            options
          }
        };
      }

      if (columnData?.data_type === 'datetime') {
        return {
          kind: GridCellKind.Custom,
          allowOverlay: true,
          readonly: false,
          activationBehaviorOverride: 'single-click',
          copyData: cellData ? formatDateLocal(cellData) : '',
          data: {
            name: 'datetime',
            value: cellData
          }
        };
      }

      return {
        kind: GridCellKind.Text,
        allowOverlay: columnData.id === 'created_at' ? false : true,
        readonly: columnData.id === 'created_at' ? true : false,
        displayData: cellData,
        allowWrapping: true,
        data: cellData
      };
    },
    [data, currentColumns]
  );

  const workflowButtonRenderer = {
    kind: GridCellKind.Custom,

    isMatch: (cell: CustomCell<any>): cell is CustomCell<any> => {
      return cell.kind === GridCellKind.Custom && cell.data?.name === 'workflow';
    },
    draw: (args: any) => {
      const { cell, ctx, rect } = args;
      const { x, y, width, height } = rect;

      if (cell?.data?.workFlowLog) {
        const statusText = cell?.data?.workFlowLog?.status;

        let backgroundColor = '#d4edda';
        let textColor = '#155724';

        if (statusText?.toLowerCase() === 'failed') {
          backgroundColor = '#f8d7da';
          textColor = '#721c24';
        }

        if (statusText?.toLowerCase() === 'in_progress') {
          backgroundColor = '#FFD8A8';
          textColor = '#B86E00';
        }

        const badgePadding = 10;
        const badgeHeight = 20;
        const badgeWidth = ctx.measureText(statusText).width + 2 * badgePadding;
        const badgeX = x + (width - badgeWidth) / 3;
        const badgeY = y + (height - badgeHeight) / 2;

        ctx.fillStyle = backgroundColor;
        ctx.beginPath();
        const radius = badgeHeight / 2;
        ctx.moveTo(badgeX + radius, badgeY);
        ctx.arcTo(badgeX + badgeWidth, badgeY, badgeX + badgeWidth, badgeY + badgeHeight, radius);
        ctx.arcTo(badgeX + badgeWidth, badgeY + badgeHeight, badgeX, badgeY + badgeHeight, radius);
        ctx.arcTo(badgeX, badgeY + badgeHeight, badgeX, badgeY, radius);
        ctx.arcTo(badgeX, badgeY, badgeX + badgeWidth, badgeY, radius);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(statusText.replace(/_/g, ' '), badgeX + badgeWidth / 2, badgeY + badgeHeight / 2);

        if (statusText?.toLowerCase() === 'failed') {
          const buttonWidth = 50;
          const buttonHeight = 20;
          const buttonX = badgeX + badgeWidth + 10;
          const buttonY = badgeY;

          ctx.fillStyle = '#00860D';
          ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);

          ctx.fillStyle = 'white';
          ctx.fillText('Retry', buttonX + buttonWidth / 2, buttonY + buttonHeight / 2);

          cell.data.buttonRect = { x: buttonX, y: buttonY, width: buttonWidth, height: buttonHeight };
        }
        return true;
      } else {
        const centerX = x + width / 2;
        const centerY = y + height / 2;
        const radius = Math.min(width, height) / 2 - 5;
        ctx.fillStyle = theme.palette.primary.main;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.moveTo(centerX - radius / 3, centerY - radius / 3);
        ctx.lineTo(centerX - radius / 3, centerY + radius / 3);
        ctx.lineTo(centerX + radius / 2, centerY);
        ctx.closePath();
        ctx.fill();
        return true;
      }
    },
    onClick: (args: any) => {
      const columnId = args.cell.data.columnId;
      const rowId = args.cell.data.rowId;
      handleExecuteWorkflow({ limit: 'single', column_id: Number(columnId), row_id: rowId });
    }
  };

  const interactionRenderer = {
    kind: GridCellKind.Custom,

    isMatch: (cell: CustomCell<any>): cell is CustomCell<any> => {
      return cell.kind === GridCellKind.Custom && cell.data?.name === 'interaction';
    },

    draw: ({ ctx, rect, cell }: any) => {
      const hasId = Boolean(cell?.data?.id);
      const { x, y, width, height } = rect;
      const size = 24;
      const iconX = x + (width - size) / 2;
      const iconY = y + (height - size) / 2;

      ctx.save();
      ctx.globalAlpha = hasId ? 1 : 0.5;
      ctx.translate(iconX, iconY);
      ctx.fillStyle = '#777777';
      const path = new Path2D(
        'M9.75781 16.0229L16.0463 11.9844L9.75781 7.94587V16.0229ZM12.0096 21.4844C10.6956 21.4844 9.46048 21.235 8.30431 20.7364C7.14814 20.2377 6.14248 19.561 5.28731 18.7061C4.43215 17.8513 3.75506 16.846 3.25606 15.6904C2.75723 14.5347 2.50781 13.3 2.50781 11.9861C2.50781 10.6721 2.75715 9.43704 3.25581 8.28087C3.75448 7.12471 4.43123 6.11904 5.28606 5.26387C6.1409 4.40871 7.14615 3.73162 8.30181 3.23262C9.45748 2.73379 10.6922 2.48438 12.0061 2.48438C13.3201 2.48438 14.5551 2.73371 15.7113 3.23237C16.8675 3.73104 17.8731 4.40779 18.7283 5.26262C19.5835 6.11746 20.2606 7.12271 20.7596 8.27837C21.2584 9.43404 21.5078 10.6688 21.5078 11.9826C21.5078 13.2966 21.2585 14.5317 20.7598 15.6879C20.2611 16.844 19.5844 17.8497 18.7296 18.7049C17.8747 19.56 16.8695 20.2371 15.7138 20.7361C14.5581 21.235 13.3234 21.4844 12.0096 21.4844ZM12.0078 19.9844C14.2411 19.9844 16.1328 19.2094 17.6828 17.6594C19.2328 16.1094 20.0078 14.2177 20.0078 11.9844C20.0078 9.75104 19.2328 7.85937 17.6828 6.30937C16.1328 4.75937 14.2411 3.98437 12.0078 3.98437C9.77448 3.98437 7.88281 4.75937 6.33281 6.30937C4.78281 7.85937 4.00781 9.75104 4.00781 11.9844C4.00781 14.2177 4.78281 16.1094 6.33281 17.6594C7.88281 19.2094 9.77448 19.9844 12.0078 19.9844Z'
      );
      ctx.fill(path);
      ctx.restore();
      return true;
    },

    onClick: ({ cell }: any) => {
      const id = cell.data.id;
      if (!id) return;
      handleOpenCallLogModal(String(id));
    }
  };

  const onHeaderMenuClick = useCallback(
    (col: number, bounds: Rectangle) => {
      setMenu({ col, bounds });
      const columnId = currentColumns[col]?.id;
      setColumnID(columnId);
    },
    [currentColumns]
  );

  const onRowAppended = useCallback(() => {
    const newPosition = totalRowCount + 1;
    handleAddRow({ row: { position: newPosition } });
  }, [handleAddRow, totalRowCount]);

  const onVisibleRegionChanged = useCallback(
    (range: Rectangle) => {
      const visibleBottom = range.y + range.height;
      if (visibleBottom >= data.length - 5 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [data.length, fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const onCellEdited = useCallback(
    async (cell: Item, newValue: GridCell) => {
      const [colIndex, rowIndex] = cell;
      if (colIndex === 0) return;

      const columnKey = currentColumns[colIndex]?.id;
      const dataRow = data[rowIndex];
      const rowId = dataRow?.row_id;
      if (!columnKey || !rowId) return;

      let nextValue = '';
      if (newValue.kind === GridCellKind.Text) {
        nextValue = newValue.data;
      } else if (newValue.kind === GridCellKind.Number) {
        nextValue = newValue.data === undefined || newValue.data === null ? '' : String(newValue.data);
      } else if (newValue.kind === GridCellKind.Custom) {
        const customData = newValue.data as { name?: string; value?: string };
        if (customData?.name === 'single_select' || customData?.name === 'datetime') {
          nextValue = customData.value || '';
        } else {
          return;
        }
      } else {
        return;
      }

      setData((prevData) => {
        const updatedData = [...prevData];
        const row = updatedData[rowIndex];
        if (row) {
          row[columnKey] = nextValue;
        }
        return updatedData;
      });

      handleUpdateCell({
        rowId: columnKey,
        columnId: rowId,
        payload: {
          cell: {
            value: nextValue
          }
        }
      });
    },
    [currentColumns, data, handleUpdateCell]
  );

  const onColMoved = useCallback(
    (startIndex: number, endIndex: number): void => {
      if (startIndex === 0) return;
      if (currentColumns[startIndex]?.is_default) {
        console.warn('Cannot move default columns');
        return;
      }

      const columnId = currentColumns[startIndex].id!;
      handleUpdateColumn({
        columnId,
        payload: {
          column: {
            position: endIndex
          }
        }
      });
      setCurrentColumns((old) => {
        const newCols = [...old];
        const [toMove] = newCols.splice(startIndex, 1);
        newCols.splice(endIndex, 0, toMove);
        return newCols;
      });
    },
    [currentColumns, handleUpdateColumn]
  );

  const onRowMoved = useCallback(
    (startIndex: number, endIndex: number): void => {
      if (startIndex === endIndex) return;
      const rowId = String(data[startIndex]?.row_id!);
      handleUpdateRow({
        rowId,
        payload: {
          row: {
            position: endIndex + 1
          }
        }
      });
      const movedRow = data[startIndex];

      const updatedData = [...data];
      updatedData.splice(startIndex, 1);

      updatedData.splice(endIndex, 0, movedRow);

      setData(updatedData);
    },
    [data, setData, handleUpdateRow]
  );

  const onColProposeMove = useCallback((_startIndex: number, endIndex: number): boolean => {
    return endIndex !== 1;
  }, []);

  const debouncedUpdateColumn = useMemo(
    () =>
      debounce((columnId, newSize) => {
        handleUpdateColumn({
          columnId,
          payload: {
            column: {
              configuration: {
                width: newSize
              }
            }
          }
        });
      }, 500),
    [handleUpdateColumn]
  );

  const onColumnResize = useCallback(
    (column: GridColumn, newSize: number, colIndex: number) => {
      const columnId = column?.id;
      debouncedUpdateColumn(columnId, newSize);
      setCurrentColumns((prevColumns) => {
        const updatedColumns = [...prevColumns];
        updatedColumns[colIndex] = { ...column, width: newSize };
        return updatedColumns;
      });
    },
    [debouncedUpdateColumn]
  );

  const handleSelectRow = (d: GridSelection) => {
    const rowIds: number[] = d.rows
      .toArray()
      .map((rowIndex) => data[rowIndex]?.row_id)
      .filter((id): id is number => id !== undefined);
    setSelectedRows(rowIds);
    setSelection(d);
  };

  const handleEditColumn = () => {
    const column = currentColumns.find((col) => col.id === columnID);
    if (column) {
      setDrawerInitialData({
        name: column.title,
        data_type: column.data_type,
        description: column.description || '',
        required: column.required || false,
        options: column.options || []
      });
      setDrawerOpen(true);
      setMenu(undefined);
    }
  };

  const handleDrawerSubmit = async (drawerData: ColumnFormData) => {
    await handleUpdateColumn({
      columnId: columnID!,
      payload: {
        column: {
          name: drawerData.name,
          ...(drawerData.data_type === 'text'
            ? {
                description: drawerData.description,
                required: drawerData.required
              }
            : {}),
          ...(drawerData.data_type === 'single_select'
            ? {
                options: drawerData.options
              }
            : {})
        }
      }
    });
    setDrawerOpen(false);
    setDrawerInitialData(null);
  };

  const handleHideColumn = () => {
    handleUpdateColumn({
      columnId: columnID!,
      payload: {
        column: {
          hidden: true
        }
      }
    });
    setMenu(undefined);
  };

  const getDynamicRowHeight = useCallback(
    (rowIndex: number) => {
      const row = data[rowIndex];
      if (!row) return selectedHeight;

      let maxLines = 1;

      currentColumns.forEach((col) => {
        const value = String(row[col.id] ?? '');

        // approximate chars per line based on column width
        const charsPerLine = Math.floor((col.width || 200) / 8);

        const lines = Math.ceil(value.length / charsPerLine);
        maxLines = Math.max(maxLines, lines);
      });

      const lineHeight = 20;
      const padding = 16;

      return Math.max(selectedHeight, maxLines * lineHeight + padding);
    },
    [data, currentColumns, selectedHeight]
  );

  if (!worksheetData && !currentColumns.length) {
    return <EmptyStateView />;
  }

  return (
    <>
      <Box maxHeight='100%'>
        <DataEditor
          className='worksheet_height'
          width='100%'
          smoothScrollX
          smoothScrollY
          rowMarkers={{
            kind: 'both',
            checkboxStyle: 'square'
          }}
          rowSelect='multi'
          rowSelectionMode='multi'
          onGridSelectionChange={handleSelectRow}
          gridSelection={selection}
          getCellContent={getCellContent}
          onHeaderMenuClick={onHeaderMenuClick}
          columns={currentColumns}
          headerIcons={customHeaderIcons}
          freezeColumns={handleFreezeColumnChange}
          customRenderers={[workflowButtonRenderer, interactionRenderer, singleSelectRenderer, datetimeRenderer]}
          rows={totalRowCount}
          onRowAppended={onRowAppended}
          onRowMoved={onRowMoved}
          onCellEdited={onCellEdited}
          onVisibleRegionChanged={onVisibleRegionChanged}
          rowHeight={(index: number) => {
            const isTrailingRow = index === totalRowCount;

            return isTrailingRow ? 50 : getDynamicRowHeight(index);
          }}
          onColumnResize={onColumnResize}
          onColumnMoved={onColMoved}
          onColumnProposeMove={onColProposeMove}
          columnSelectionBlending='mixed'
          rangeSelectionBlending='mixed'
          isOutsideClick={(event) => {
            const target = event.target as HTMLElement | null;
            // Keep the cell editor open while interacting with MUI date/time poppers
            if (target?.closest?.('.MuiPickersPopper-root, .MuiDialog-root, .MuiModal-root, .click-outside-ignore')) {
              return false;
            }
            return true;
          }}
        />
        {isOpen &&
          renderLayer(
            <Box {...layerProps} className='menu'>
              <Paper sx={{ width: 220, maxWidth: '100%', mt: 0.5, mr: 0.5, border: '1px solid #ECECEC' }}>
                <MenuList>
                  <MenuItem onClick={handleEditColumn}>
                    <ListItemIcon>
                      <MdOutlineEdit size={18} />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography fontSize={14}>Edit Column</Typography>
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleHideColumn}>
                    <ListItemIcon>
                      <FaRegEyeSlash size={18} color='rgba(0, 0, 0, 0.54)' />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography fontSize={14}>Hide Column</Typography>
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleDeleteColumn(String(columnID))}>
                    <ListItemIcon>
                      <RiDeleteBin6Line color='red' size={14} />
                    </ListItemIcon>
                    <ListItemText sx={{ color: 'red' }}>
                      <Typography fontSize={14}>Delete</Typography>
                    </ListItemText>
                  </MenuItem>
                </MenuList>
              </Paper>
            </Box>
          )}
      </Box>
      <Box position='fixed' left='79px' bottom='0' bgcolor='white' p={2} minWidth='95%'>
        <Box display='flex' justifyContent='space-between' alignItems='center' gap='10px'>
          <Box bgcolor='#F8F9FA'>
            <Button
              sx={{ width: '130px' }}
              onClick={() => {
                if (selectedRows.length > 0) {
                  handleDeleteRow({ ids: selectedRows });
                } else {
                  onRowAppended();
                }
                setSelectedRows([]);
                setSelection({ columns: CompactSelection.empty(), rows: CompactSelection.empty() });
              }}
            >
              {selectedRows.length > 0 ? 'Delete Row' : '+ Add Row'}
            </Button>
          </Box>
          <Box display='flex' gap='10px'>
            <Typography color='text.secondary' fontSize='14px'>
              {data.length || 0} Rows{' '}
            </Typography>
            <Typography color='text.secondary' fontSize='14px'>
              {columnsData?.data?.length || 0}/{columnsData?.data?.length || 0} Columns{' '}
            </Typography>
          </Box>
        </Box>
      </Box>

      <CallLogForm logId={callLogId} open={callLogDrawerOpen} onClose={() => setCallLogDrawerOpen(false)} />

      <ColumnDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerInitialData(null);
        }}
        onSubmit={handleDrawerSubmit}
        initialData={drawerInitialData}
        mode='edit'
      />
    </>
  );
}
